// 统一身份认证 IdentityZen。管理员在 /admin/login 浏览器直连这里拿 RS256 token。
const BASE = import.meta.env.DEV ? '/identityzen' : 'https://auth.nju.top';
const FP_KEY = 'blackboard.deviceFingerprint';

function getOrCreateDeviceFingerprint(): string {
  try {
    let fp = localStorage.getItem(FP_KEY);
    if (!fp) {
      fp = (crypto.randomUUID?.() ?? `fp-${Date.now()}-${Math.random().toString(36).slice(2)}`);
      localStorage.setItem(FP_KEY, fp);
    }
    return fp;
  } catch {
    return `fp-ephemeral-${Date.now()}`;
  }
}

function friendlyLoginError(status: number): string {
  if (status === 400) return '账号或密码格式不正确，请检查后重试。';
  if (status === 401 || status === 403) return '账号或密码错误。';
  if (status === 404) return '账号不存在，请确认是否为校内账号。';
  if (status === 409) return '该账号已达设备上限，请先在其它设备登出后再试。';
  if (status === 429) return '尝试次数过多，请稍后再试。';
  if (status >= 500) return '认证服务暂时不可用，请稍后再试。';
  return `登录失败（错误码 ${status}）。`;
}

export interface PillTokens {
  accessToken: string;
  refreshToken: string;
}

interface PillErrorBody {
  error?: string;
  message?: string;
}

/** Map IdentityZen error body → human text; null = no recognizable message. */
function pillErrorText(body: unknown): string | null {
  const b = body as PillErrorBody | null;
  if (!b || typeof b !== 'object') return null;
  const code = b.error ?? b.message ?? '';
  if (!code) return null;
  const s = String(code);
  if (/USERNAME_ALREADY|ALREADY_EXISTS|EXISTS/i.test(s)) return '用户名已被占用，换一个试试。';
  if (/INVALID_USERNAME|USERNAME_FORMAT|BAD_REQUEST/i.test(s)) return '用户名或密码格式不正确（用户名建议字母开头，6-32 位）。';
  if (/INVALID_PASSWORD|WEAK_PASSWORD|PASSWORD_FORMAT/i.test(s)) return '密码强度不足（建议至少 8 位，含字母和数字）。';
  return s.slice(0, 120);
}

async function pillPasswordAuth(
  path: '/v1/auth/password/login' | '/v1/auth/password/register',
  username: string,
  password: string,
): Promise<PillTokens | { error: string }> {
  try {
    const res = await fetch(`${BASE}${path}`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({
        username,
        password,
        deviceFingerprint: getOrCreateDeviceFingerprint(),
        deviceName: 'Blackboard Web',
        appId: 'blackboard',
      }),
    });
    if (!res.ok) {
      let body: unknown = null;
      try { body = await res.json(); } catch { /* non-JSON */ }
      const mapped = pillErrorText(body);
      if (mapped) return { error: mapped };
      if (path.endsWith('/register')) {
        return { error: friendlyLoginError(res.status) };
      }
      // Login-specific hint: account may simply not exist yet.
      if (res.status === 401 || res.status === 404) {
        return { error: '账号或密码错误；还没注册的话点下方「注册新账号」。' };
      }
      return { error: friendlyLoginError(res.status) };
    }
    const data = await res.json();
    const accessToken = data.accessToken ?? data.access_token;
    const refreshToken = data.refreshToken ?? data.refresh_token;
    if (!accessToken) return { error: '认证响应缺少令牌字段。' };
    return { accessToken, refreshToken };
  } catch {
    return { error: '网络异常，请检查网络后重试。' };
  }
}

export function loginPassword(username: string, password: string) {
  return pillPasswordAuth('/v1/auth/password/login', username, password);
}

/** Register-and-login in one shot (IdentityZen returns tokens on register). */
export function registerPassword(username: string, password: string) {
  return pillPasswordAuth('/v1/auth/password/register', username, password);
}

/** Send an SMS code; IdentityZen auto-provisions unknown phones on verify. */
export async function smsSend(phone: string): Promise<{ smsToken: string } | { error: string }> {
  try {
    const res = await fetch(`${BASE}/v1/auth/sms/send`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ phone, appId: 'blackboard' }),
    });
    if (!res.ok) {
      let body: unknown = null;
      try { body = await res.json(); } catch { /* non-JSON */ }
      return { error: pillErrorText(body) ?? friendlyLoginError(res.status) };
    }
    const data = await res.json();
    if (!data?.smsToken) return { error: '短信服务响应异常，请稍后再试。' };
    return { smsToken: data.smsToken };
  } catch {
    return { error: '网络异常，请检查网络后重试。' };
  }
}

export async function smsVerify(
  phone: string,
  code: string,
  smsToken: string,
): Promise<PillTokens | { error: string }> {
  try {
    const res = await fetch(`${BASE}/v1/auth/sms/verify`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({
        phone,
        code,
        smsToken,
        deviceFingerprint: getOrCreateDeviceFingerprint(),
        deviceName: 'Blackboard Web',
        appId: 'blackboard',
      }),
    });
    if (!res.ok) {
      let body: unknown = null;
      try { body = await res.json(); } catch { /* non-JSON */ }
      const mapped = pillErrorText(body);
      if (mapped) return { error: mapped };
      if (res.status === 401) return { error: '验证码错误或已过期，请重试。' };
      return { error: friendlyLoginError(res.status) };
    }
    const data = await res.json();
    const accessToken = data.accessToken ?? data.access_token;
    const refreshToken = data.refreshToken ?? data.refresh_token;
    if (!accessToken) return { error: '认证响应缺少令牌字段。' };
    return { accessToken, refreshToken };
  } catch {
    return { error: '网络异常，请检查网络后重试。' };
  }
}

export async function fetchMe(token: string) {
  try {
    const res = await fetch('/api/me', { headers: { authorization: `Bearer ${token}` } });
    if (!res.ok) return null;
    return await res.json();
  } catch {
    return null;
  }
}
